import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Plus, SearchCheck, Trash2 } from 'lucide-react';
import { normalizeTerm } from '@autopilot/contracts';
import { api, type ExclusionProbeResponse } from '../../api/client';
import { qk } from '../../api/hooks';
import { ApiError, isConflict } from '../../api/errors';
import { EmptyState, ErrorAlert, QueryBoundary } from '../../components/States';
import { ToneBadge } from '../../components/StatusBadge';
import { SYNC_STATE_LABEL } from '../../lib/labels';
import { formatDateTime } from '../../lib/format';

export function ExclusionTab() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: qk.exclusion,
    queryFn: api.exclusionList,
    refetchInterval: (query) => (query.state.data?.syncState === 'PENDING' ? 2000 : false),
  });
  const [input, setInput] = useState('');
  const [probeText, setProbeText] = useState('');
  const [probe, setProbe] = useState<ExclusionProbeResponse | null>(null);
  const normalized = normalizeTerm(input);
  const duplicate = !!q.data?.terms.some((t) => t.normalized === normalized);

  const onConflict = (err: unknown) => {
    if (isConflict(err)) void qc.invalidateQueries({ queryKey: qk.exclusion });
  };
  const add = useMutation({
    mutationFn: () => api.addExclusionTerm(input, q.data!.version),
    onSuccess: (d) => {
      qc.setQueryData(qk.exclusion, d);
      setInput('');
    },
    onError: onConflict,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.removeExclusionTerm(id, q.data!.version),
    onSuccess: (d) => qc.setQueryData(qk.exclusion, d),
    onError: onConflict,
  });
  const runProbe = useMutation({ mutationFn: () => api.probeExclusion(probeText), onSuccess: setProbe });
  const fe = add.error instanceof ApiError ? add.error.fieldErrors : {};

  return (
    <QueryBoundary query={q} action="Negativliste laden" loadingRows={5}>
      {q.data && (
        <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' }, alignItems: 'start' }}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                <Typography variant="h6" component="h3">
                  Begriffe
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Versionsstand v{q.data.version}
                </Typography>
                <ToneBadge tone={SYNC_STATE_LABEL[q.data.syncState].tone} label={SYNC_STATE_LABEL[q.data.syncState].label} spinning={q.data.syncState === 'PENDING'} />
              </Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Teilwortsuche:</strong> Ein Begriff trifft überall, wo er als Zeichenfolge vorkommt – auch in zusammengesetzten Wörtern und unabhängig von Groß-/Kleinschreibung („schaden“ trifft „Wasserschaden“). Geprüft werden Betreff, Beschreibung, der vollständige Verlauf und lesbare Anhänge, bevor ein Modell aufgerufen wird. Keine regulären Ausdrücke.
              </Alert>
              <Box
                component="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (normalized && !duplicate) add.mutate();
                }}
                sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}
              >
                <TextField
                  label="Neuer Begriff"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  error={!!fe.input || duplicate}
                  helperText={fe.input ?? (duplicate ? 'Diese Normalform ist bereits enthalten.' : input ? `Normalform: „${normalized}“` : 'Leere Begriffe sind nicht erlaubt.')}
                  sx={{ flex: '1 1 260px' }}
                  slotProps={{ htmlInput: { maxLength: 200 } }}
                />
                <Button type="submit" variant="contained" startIcon={<Plus size={16} />} disabled={!normalized || duplicate || add.isPending} sx={{ mt: 0.25 }}>
                  Hinzufügen
                </Button>
              </Box>
              {(add.error && !Object.keys(fe).length) || remove.error ? (
                <Box sx={{ mt: 1.5 }}>
                  <ErrorAlert error={add.error ?? remove.error} action={add.error ? 'Begriff hinzufügen' : 'Begriff entfernen'} />
                </Box>
              ) : null}
              {q.data.terms.length === 0 ? (
                <EmptyState title="Keine Begriffe" description="Die Negativliste ist leer." />
              ) : (
                <Box sx={{ overflowX: 'auto', mt: 2 }}>
                  <Table size="small" aria-label="Negativliste">
                    <TableHead>
                      <TableRow>
                        <TableCell>Eingabe</TableCell>
                        <TableCell>Normalform</TableCell>
                        <TableCell>Angelegt</TableCell>
                        <TableCell align="right">
                          <span className="visually-hidden">Aktionen</span>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {q.data.terms.map((t) => (
                        <TableRow key={t.id}>
                          <TableCell>{t.input}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{t.normalized}</TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {t.createdBy} · {formatDateTime(t.createdAt)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Begriff entfernen">
                              <IconButton size="small" aria-label={`Begriff ${t.input} entfernen`} onClick={() => remove.mutate(t.id)} disabled={remove.isPending}>
                                <Trash2 size={16} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" component="h3">
                Trefferprobe
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Prüft einen Beispieltext gegen die aktuelle Negativliste – ohne Modellaufruf und ohne Kosten.
              </Typography>
              <TextField label="Beispieltext" value={probeText} onChange={(e) => setProbeText(e.target.value)} multiline minRows={5} fullWidth />
              <Button sx={{ mt: 1.5 }} variant="outlined" startIcon={<SearchCheck size={16} />} onClick={() => runProbe.mutate()} disabled={!probeText.trim() || runProbe.isPending}>
                Probe ausführen
              </Button>
              <Box sx={{ mt: 2 }} aria-live="polite">
                {runProbe.error ? <ErrorAlert error={runProbe.error} action="Trefferprobe" /> : null}
                {probe &&
                  (probe.hits.length === 0 ? (
                    <Alert severity="success">Kein Treffer – der Text würde nicht durch die Negativliste ausgeschlossen.</Alert>
                  ) : (
                    <Alert severity="warning" data-testid="probe-hits">
                      {probe.hits.length} {probe.hits.length === 1 ? 'Treffer' : 'Treffer'}: {probe.hits.map((h) => `„${h.term}“ (Position ${h.index + 1})`).join(', ')}. Ein solches Ticket wird vor jedem Modellaufruf ausgeschlossen.
                    </Alert>
                  ))}
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </QueryBoundary>
  );
}
